/* A Mixcloud show, as a link out. Deliberately not an embed.
 *
 * WHY THERE IS NO IFRAME HERE
 * ---------------------------
 * Mixcloud's Terms of Use §4 prohibit use of the widget for "any form of
 * commercial use OR non personal / individual use (unless pursuant to prior
 * written consent…)". Two prongs joined by OR, and that OR is the whole
 * problem: being a non-profit answers the first prong and does nothing at all
 * to the second, because a station website is not a personal or individual
 * website. So the widget is off-limits to us on the second prong alone, and no
 * change to the station's legal shape rescues it.
 *
 * The iframe does technically render — Mixcloud serves it to anyone who asks.
 * That makes this UNENFORCED, not permitted, and an unenforced prohibition is
 * still the thing you are relying on when someone reads the terms.
 *
 * Kiosk Radio's site links out to mixcloud.com show pages and embeds nothing.
 * That is the pattern this component implements, and it sidesteps §4 entirely:
 * a hyperlink to a public page is not "use of the widget" under any reading.
 *
 * IT IS NEVER AN <img> EITHER. The sleeve is the same palette field the rest of
 * the site uses (see Artwork.tsx) rather than Mixcloud's own artwork: pulling
 * their image would put a remote asset on the page for no gain, and the design
 * does not use photographic artwork anywhere.
 */

import type { CSSProperties } from 'react';

import type { ColorToken } from '../types';
import { Artwork } from './Artwork';

/* Read once, into a plain const, so esbuild can see a comparison between two
   string literals and fold the whole flag to `false` at build time. That is
   worth arranging: with the flag folded, the branch that builds a
   mixcloud.com/widget URL is eliminated from the shipped bundle rather than
   merely never taken, and the prohibited widget is not in the artefact at all.
   Confirm with `grep mixcloud.com/widget web/dist/assets/*.js` after a build —
   the default build must not match. */
const CONSENT_ENV: unknown = import.meta.env.VITE_MIXCLOUD_EMBED_CONSENT;

/**
 * Whether we hold Mixcloud's PRIOR WRITTEN CONSENT to embed their widget
 * (Terms of Use §4, quoted above).
 *
 * DEFAULTS TO FALSE, and must stay false until that consent is on file. The
 * day it arrives this is a one-line change and nothing else moves: set
 * `VITE_MIXCLOUD_EMBED_CONSENT=true` in the build environment, and the one
 * place that branches on it (pages/Episode.tsx) starts drawing the iframe.
 *
 * Compared against exact strings rather than read as a boolean because Vite
 * substitutes env vars TEXTUALLY: `import.meta.env.VITE_X` is the string
 * "false" when the variable is set to false, and `Boolean("false")` is true.
 * That is the classic way a flag like this defaults to on by accident, and
 * this one defaulting to on is a terms breach on every episode page.
 *
 * Exact and lowercase, deliberately: "TRUE" and " true " do not count. A flag
 * whose whole job is to stay off until a lawyer says otherwise should be
 * turned on by typing the value it documents, not by anything near it.
 */
export const MIXCLOUD_EMBED_CONSENT: boolean = CONSENT_ENV === 'true' || CONSENT_ENV === '1';

export interface MixcloudCardProps {
  /** The show page on mixcloud.com. Rendered as an anchor, never as a widget. */
  url: string;
  /** The episode title, as this site spells it. */
  title: string;
  /** Fills the sleeve, same as everywhere else. */
  catalogueNo: string;
  /** The palette field. Straight from `artwork_color` on the wire. */
  artworkColor: ColorToken;
  /** Overrides the small line on the sleeve. Defaults to the catalogue number. */
  sleeveLabel?: string | null;
  /** One line under the title — hosts, a date, a runtime. Whatever the page has. */
  meta?: string | null;
  className?: string;
  style?: CSSProperties;
}

const CARD: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '18px',
  flexWrap: 'wrap',
  padding: '16px',
  maxWidth: '760px',
  border: 'var(--bw) solid var(--edge)',
  background: 'var(--paper)',
};

const BODY: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '7px',
  flexGrow: 1,
  flexBasis: '220px',
  minWidth: 0,
};

export function MixcloudCard({
  url,
  title,
  catalogueNo,
  artworkColor,
  sleeveLabel,
  meta,
  className,
  style,
}: MixcloudCardProps) {
  return (
    <div className={className} style={style ? { ...CARD, ...style } : CARD}>
      <Artwork
        color={artworkColor}
        catalogueNo={catalogueNo}
        label={sleeveLabel ?? null}
        size="md"
      />

      <div style={BODY}>
        <div className="mono dim">On Mixcloud</div>
        <div className="t-h3" style={{ overflowWrap: 'anywhere' }}>
          {title}
        </div>
        {meta ? <div className="mono dim">{meta}</div> : null}
      </div>

      {/* A real anchor: it opens in a new tab so a listener does not lose the
          notes and the tracklist below, and `noopener noreferrer` because the
          opened page must not get a handle on this window. `external` is for
          the reader of the markup, not the browser. */}
      <a
        className="btn"
        href={url}
        target="_blank"
        rel="noopener noreferrer external"
        aria-label={`Listen to ${title} on Mixcloud — opens in a new tab`}
        style={{ flexShrink: 0 }}
      >
        Listen on Mixcloud →
      </a>
    </div>
  );
}
